/* eslint-disable @next/next/no-img-element */
import React from 'react';
import classNames from 'classnames';
import { handleImageUrl } from '$/utils/image';
import { getVideoCoverUrl } from '$/utils/upload';
import { getInlineFileIconConfig } from '$/components/InlineBox/constants';
import { InputPromptSkillItem } from '$/domains/caseV3/type';
import IconFont from '../IconFont';
import styles from './index.module.scss';
import { IInlineBoxData } from '../InlineBox/types';
import SkillButton from './SkillButton';

export type MediaInputSkillCardRenderProps = {
  skill: InputPromptSkillItem;
  onRemove?: (skill: InputPromptSkillItem) => void;
};

export type MediaInputProps = {
  className?: string;
  /** 受控预览列表 */
  previews?: IInlineBoxData[];
  /** 等待用户确认的单个资源占位。 */
  inactivePreview?: IInlineBoxData | null;
  /** 点击添加按钮回调，由父组件决定文件选择/上传方式 */
  onAdd?: () => void;
  /** 移除资源回调，返回被移除的项 */
  onRemove?: (item: IInlineBoxData) => void;
  /** 点击资源回调，返回被点击的项 */
  onItemClick?: (item: IInlineBoxData) => void;
  /** 点击未激活资源时请求激活。 */
  onInactiveClick?: () => void;
  /** 移除未激活资源占位。 */
  onInactiveRemove?: () => void;
  /** 是否显示加载状态 */
  isLoading?: boolean;
  /** 当前上传批次的文件数量，加载态下按数量展示占位。 */
  loadingCount?: number;
  /** 是否显示添加按钮 */
  showAddButton?: boolean;
  /** 已选 Skill 列表，展示在资源预览区下方 */
  skillList?: InputPromptSkillItem[];
  /** 移除已选 Skill 回调，返回被移除的项 */
  onSkillRemove?: (skill: InputPromptSkillItem) => void;
  /** 自定义已选 Skill 卡片渲染 */
  renderSkillCard?: (props: MediaInputSkillCardRenderProps) => React.ReactNode;
};

/** 复用现有资源缩略图规则，保证 active/inactive 卡片展示一致。 */
function MediaPreviewContent({ item }: { item: IInlineBoxData; }) {
  const isFile = item.mediaType === 'file';
  const isVideo = item.mediaType === 'video';
  if (isFile) {
    const { icon, background, iconColor } = getInlineFileIconConfig(item.url);
    return (
      <div className={styles['icon-box']} style={{ background }}>
        <IconFont name={icon} fontSize={18} color={iconColor} />
      </div>
    );
  }
  return (
    <>
      <img
        src={handleImageUrl(
          item.coverUrl || (isVideo ? getVideoCoverUrl(item.url) : item.url),
          { size: 160 },
        )}
        alt=""
        loading="lazy"
        className={styles.previewImage}
        crossOrigin="anonymous"
      />
      {isVideo && (
        <span className={styles.videoMask}>
          <IconFont name="playFill" fontSize={16} color="#fff" />
        </span>
      )}
    </>
  );
}

/**
 * 渲染输入框上方的资源和已选 Skill 预览区。
 */
function MediaInput({
  className,
  previews = [],
  inactivePreview,
  skillList = [],
  onAdd,
  onRemove,
  onItemClick,
  onInactiveClick,
  onInactiveRemove,
  isLoading,
  loadingCount,
  showAddButton = false,
  onSkillRemove,
  renderSkillCard,
}: MediaInputProps) {
  const hasMediaPreview = showAddButton || previews.length > 0 || !!inactivePreview || isLoading;
  const hasSkillPreview = skillList.length > 0;
  const loadingPreviewCount = isLoading ? Math.max(1, loadingCount ?? 1) : 0;
  const previewListRef = React.useRef<HTMLDivElement>(null);
  const inactivePreviewKey = inactivePreview?.index || inactivePreview?.url || '';
  const previewItemCount = previews.length + loadingPreviewCount + (inactivePreview ? 1 : 0);
  const previousPreviewStateRef = React.useRef({ itemCount: 0, inactivePreviewKey: '' });

  /** 新增资源或替换画布占位后，将最新预览项移动到可视区域。 */
  React.useEffect(() => {
    const previousState = previousPreviewStateRef.current;
    const shouldScrollToEnd = previewItemCount > previousState.itemCount
      || (!!inactivePreviewKey && inactivePreviewKey !== previousState.inactivePreviewKey);
    previousPreviewStateRef.current = { itemCount: previewItemCount, inactivePreviewKey };
    if (!shouldScrollToEnd || !previewListRef.current) return;
    previewListRef.current.scrollLeft = previewListRef.current.scrollWidth;
  }, [inactivePreviewKey, previewItemCount]);

  return (
    <div className={classNames(styles.mediaInput, className, 'MediaPreviewContainer')}>
      {hasMediaPreview && (
      <div className={classNames(styles.mediaPreview, 'MediaPreview')}>

        {showAddButton && (
        <div className={styles.uploadButton} onClick={onAdd} aria-label="添加素材">
          <IconFont name="plusBlack" fontSize={16} color="currentColor" />
        </div>
        )}

        <div ref={previewListRef} className={styles.previewList}>
          {previews.map((item) => {
            return (
              <div key={item.index || item.url} className={styles.previewItem} onClick={() => onItemClick?.(item)}>
                <MediaPreviewContent item={item} />
                <div
                  className={styles.removeButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove?.(item);
                  }}
                  aria-label="移除素材"
                >
                  <IconFont name="crossFill" fontSize={10} color="currentColor" />
                </div>
              </div>
            );
          })}
          {inactivePreview && (
            <div
              key={`inactive-${inactivePreview.index || inactivePreview.url}`}
              className={classNames(styles.previewItem, styles.inactiveItem)}
              onClick={onInactiveClick}
            >
              <MediaPreviewContent item={inactivePreview} />
              <div
                className={styles.removeButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onInactiveRemove?.();
                }}
                aria-label="移除素材"
              >
                <IconFont name="crossFill" fontSize={10} color="currentColor" />
              </div>
            </div>
          )}
          {Array.from({ length: loadingPreviewCount }, (_, index) => (
            <div key={`upload-loading-${index}`} className={styles.loadingPreview} aria-label="上传中">
              <IconFont className={styles['uploading-icon']} name="loadingCircleBold" fontSize={18} color="currentColor" />
            </div>
          ))}
        </div>
      </div>
      )}
      {hasSkillPreview && (
      <div className={styles.skillPreview}>
        {
        skillList?.map((skill) => (
          renderSkillCard ? (
            <React.Fragment key={`${skill.category_id}-${skill.id}`}>
              {renderSkillCard({ skill, onRemove: onSkillRemove })}
            </React.Fragment>
          ) : (
            <SkillButton
              key={`${skill.category_id}-${skill.id}`}
              id={`${skill.id}`}
              name={skill.name}
              icon={skill.category_icon || 'bookBold'}
              onRemove={onSkillRemove ? () => onSkillRemove(skill) : undefined}
            />
          )
        ))
        }
      </div>
      )}
    </div>
  );
}

export default React.memo(MediaInput);
